import { colors } from '@design-system/styles'
import clsx from 'clsx'
import { isBefore } from 'date-fns'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import { useLocation } from 'react-router'
import {
  무료체험_시나리오_스와이퍼,
  무료체험_시나리오_스와이퍼_슬라이드,
} from 'src/constant/ui/DOM'
import { _mixpanelUtils } from 'src/utils/trackings/_mixpanel'

import { Icon } from '@/component/atoms/Icon'
import useScrollbars from '@/hooks/useScrollbars'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import { type ScenarioService, scenarioService } from '@/service/scenario.service'
import { localStorageService } from '@/service/storage.service'
import dateUtils from '@/utils/DateUtils'

import ProfileFolder from './_components/ProfileFolder'
import ScenarioCard from './_components/ScenarioCard'
import SubScenarioCard from './_components/SubScenarioCard'
import S from './Scenario.style'

// hook으로 뺄 경우를 대비해서 변수에 담음
const 스와이퍼_컨테이너 = `scenario-swiper-container`
const 스와이퍼_스크롤바_SELECTOR = `#${무료체험_시나리오_스와이퍼} > div`
const pathnamesToExclude = [RouteName.signatureWorkbook, RouteName.customPrint] as string[]

// 조건부 렌더링되는 컴포넌트 - 조건은 PortalContainer에서 확인. (hook 때문에 여기에 못넣음)
const Scenario = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    if (scenarioService.enabled) {
      scenarioService.fetchUserScenarioHandler()
    }
  }, [scenarioService.enabled])

  useEffect(() => {
    const localStorageData = localStorageService.getItem('무료체험_시나리오_만료') ?? {}
    if (commonRepo.user && localStorageData && commonRepo.user.loginId in localStorageData) {
      scenarioService.setProfileShow(
        !isBefore(new Date(), new Date(localStorageData[commonRepo.user.loginId])),
      )
    }
  }, [commonRepo.user])

  useEffect(() => {
    scenarioService.initialize({
      appName: commonRepo.appName,
      isSchoolflat: commonRepo.isSchoolflat,
    })
  }, [commonRepo.isSchoolflat, commonRepo.appName])

  if (pathnamesToExclude.includes(pathname)) {
    return null
  }

  return (
    <>
      <ScenarioRibbon
        show={scenarioService.isProfileShow}
        setShow={scenarioService.setProfileShow}
      />
      {scenarioService.isProfileShow && (
        <S.ScenarioContainer>
          <ScenarioProfileFolder />
          <ScenarioCardList show={scenarioService.isProfileShow} />
        </S.ScenarioContainer>
      )}
    </>
  )
}

const ScenarioRibbon = observer(
  ({ show, setShow }: { show: boolean; setShow: ScenarioService['setProfileShow'] }) => {
    return (
      <S.RibbonButton
        className={clsx('ribbon-button', show ? 'folded' : 'unfolded')}
        onClick={() => {
          if (commonRepo.user?.loginId) {
            if (show) {
              const _localStorageData = localStorageService.getItem('무료체험_시나리오_만료') ?? {}
              _localStorageData[commonRepo.user.loginId] = dateUtils.setDateByMidnight(1).getTime()
              localStorageService.setItem('무료체험_시나리오_만료')(_localStorageData)
              _mixpanelUtils.uiTrack('정복_프로필_클릭', { 동작: '닫기' })
            } else {
              const _localStorageData = localStorageService.getItem('무료체험_시나리오_만료') ?? {}
              if (commonRepo.user.loginId in _localStorageData) {
                delete _localStorageData[commonRepo.user.loginId]
              }
              localStorageService.setItem('무료체험_시나리오_만료')(_localStorageData)
              _mixpanelUtils.uiTrack('정복_프로필_클릭', { 동작: '열기' })
            }
          }

          setShow(!show)
        }}
      >
        <div className="ribbon-button-inner">
          <span className="ribbon-button-inner-text">
            <Icon name="book_opened" size={14} color="#ffffff" style={{ marginRight: 2 }} />
            {commonRepo.appKorName} 완전정복
          </span>
          {show && (
            <Icon name="chevron_left" size={24} color="#ffffff" className="chevron-left-icon" />
          )}
        </div>
      </S.RibbonButton>
    )
  },
)

const ScenarioProfileFolder = observer(() => {
  if (!commonRepo.user?.name) return null
  return (
    <div className="folder">
      <ProfileFolder />
    </div>
  )
})

const ScenarioCardList = observer(({ show }: { show: boolean }) => {
  const [navigation, setNavigation] = useState({ prev: true, next: true })
  const swiperRef = useRef({ scrollLeft: 0, left: 0, right: -1, end: 0 })
  const swiperScrollbars = useScrollbars(스와이퍼_스크롤바_SELECTOR, undefined, [show])

  const slidePrev = () => {
    swiperScrollbars.scrollSmoothTo({
      left: swiperRef.current.left,
    })
    _mixpanelUtils.uiTrack('정복_기능카드_네비게이션클릭', { 방향: '왼쪽' })
  }
  const slideNext = () => {
    swiperScrollbars.scrollSmoothTo({
      left: swiperRef.current.right,
    })
    _mixpanelUtils.uiTrack('정복_기능카드_네비게이션클릭', { 방향: '오른쪽' })
  }
  useEffect(() => {
    const container = document.querySelector(`.${스와이퍼_컨테이너}`)
    if (container && swiperScrollbars.el) {
      if (swiperRef.current) {
        swiperRef.current.end = swiperScrollbars.el.scrollWidth
      }
    }
    onScroll()
  }, [scenarioService.scenarioColl.toCheckedKeys])

  useEffect(() => {
    if (!show && swiperScrollbars.el) {
      swiperScrollbars.el.scrollLeft = 0
    }
  }, [show])

  const onScroll = useCallback(() => {
    if (swiperScrollbars.el) {
      const { scrollLeft, clientWidth } = swiperScrollbars.el
      setNavigation({
        prev: scrollLeft !== 0,
        next: scrollLeft + clientWidth !== swiperRef.current.end,
      })
      const diff = scrollLeft % 266
      swiperRef.current.left = diff === 0 ? scrollLeft - 266 : scrollLeft - diff
      swiperRef.current.right = scrollLeft + (266 - diff)
      swiperRef.current.scrollLeft = scrollLeft
    }
  }, [swiperScrollbars.el])

  return (
    <>
      {navigation.prev && (
        <button className="navigation-button navigation-prev" onClick={slidePrev}>
          <Icon name="chevron_left" size={20} color={colors.gray.$600} />
        </button>
      )}
      {navigation.next && (
        <button className="navigation-button navigation-next" onClick={slideNext}>
          <Icon name="chevron_right" size={20} color={colors.gray.$600} />
        </button>
      )}
      <div className={스와이퍼_컨테이너}>
        <Scrollbars tagName="ul" id={무료체험_시나리오_스와이퍼} onScrollStop={onScroll}>
          {scenarioService.progressRate === 100 && (
            <>
              {scenarioService.bonusScenarioColl.toArr.map((bonusScenario) => (
                <li className={무료체험_시나리오_스와이퍼_슬라이드} key={bonusScenario.id}>
                  <ScenarioCard
                    scenario={bonusScenario}
                    status={scenarioService.getScenarioStatus(bonusScenario.id)}
                  />
                </li>
              ))}
              {scenarioService.subScenarioColl.toArr.map((subScenario) => (
                <li className={무료체험_시나리오_스와이퍼_슬라이드} key={subScenario.id}>
                  <SubScenarioCard subScenario={subScenario} />
                </li>
              ))}
            </>
          )}
          {scenarioService.scenarioColl.toArr.map((scenario) => (
            <li className={무료체험_시나리오_스와이퍼_슬라이드} key={scenario.id}>
              <ScenarioCard
                scenario={scenario}
                status={scenarioService.getScenarioStatus(scenario.id)}
              />
            </li>
          ))}
        </Scrollbars>
      </div>
    </>
  )
})
export default observer(Scenario)
